import Vue from 'vue'
import Router from 'vue-router'
import { routes } from './routes'
import { doLogin } from '../plugins/authentication'
import store from '../store/main'
import { accessRedirect } from './assetClassAccess'
import { getSelectedAssetClass } from '../utils/assetClassState'
import { routerBaseFor } from '../utils/assetClassSlugs'

Vue.use(Router)

// The asset class this router was built for. The base is fixed for the whole page load — switching
// asset class reloads the page — and makes every href the router generates (router-link, resolve,
// push) carry the /{assetClass}/ prefix by itself, while the route table stays flat. It is decided
// from the URL before user/info is known; main.js moves the page to the right prefix if the final
// answer differs.
export const routerAssetClass = getSelectedAssetClass()

const router = new Router({
  mode: 'history',
  base: routerBaseFor(routerAssetClass),
  routes: routes,
  scrollBehavior(to, from, savedPosition) {
    return { top: 0 } // always scroll to top
  },
})

// vue-router 3 gives onError only the error: the destination of the navigation in progress is kept here.
let navigatingTo = null

router.beforeEach((to, from, next) => {
  navigatingTo = to
  if (!Vue.$keycloak.authenticated) {
    doLogin()
    // The redirect to the login page has already started: closing the navigation avoids leaving
    // it pending.
    next(false)
    return
  }

  const redirect = accessRedirect(to, {
    can: (permission) => store.getters.can(permission),
    permissionsLoaded: !!store.getters.getPermissions.userPermissions,
  })
  redirect ? next(redirect) : next()
})

// After a deploy the page still open in the browser asks for lazy chunks of the previous build, which
// the server no longer has (it answers with index.html): the navigation fails with a ChunkLoadError.
// Loading the destination again fetches the new build. Once per destination, so that a chunk that is
// really missing cannot loop.
router.onError((error) => {
  const to = navigatingTo
  const isChunkError = error && (error.name === 'ChunkLoadError' || /Loading chunk .* failed/.test(error.message))
  if (!isChunkError || !to) {
    return
  }
  const reloadKey = `chunk-reload:${to.fullPath}`
  try {
    if (sessionStorage.getItem(reloadKey)) {
      sessionStorage.removeItem(reloadKey)
      return
    }
    sessionStorage.setItem(reloadKey, '1')
  } catch (e) {
    return
  }
  window.location.assign(router.resolve(to.fullPath).href)
})

// The /{assetClass}/ prefix is part of the router `base` (see above): see docs/asset-class-url-prefix.md.

export default router
